"use client"

import { useState, useRef } from "react"
import Image from "next/image"
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog"

interface SharedImageModalProps {
  isOpen: boolean
  onClose: () => void
  imageSrc?: string
  imageAlt?: string
}

export function SharedImageModal({ isOpen, onClose, imageSrc, imageAlt }: SharedImageModalProps) {
  const [isLoading, setIsLoading] = useState(false)
  const [renderSrc, setRenderSrc] = useState("")
  const [retry, setRetry] = useState(0)
  const clickTokenRef = useRef(0)

  // Reset state when modal opens with new image
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      onClose()
      clickTokenRef.current += 1
      setRenderSrc("")
      setRetry(0)
      setIsLoading(false)
    } else if (open && imageSrc) {
      // Preload the full image when modal opens
      clickTokenRef.current += 1
      const clickId = clickTokenRef.current
      
      setIsLoading(true)
      setRetry(0)
      setRenderSrc("")

      const pre = new window.Image()
      pre.src = imageSrc

      let didFinish = false
      const onReady = () => {
        if (didFinish) return
        didFinish = true
        if (clickId !== clickTokenRef.current) return
        setRenderSrc(imageSrc)
        setIsLoading(false)
      }

      const timeout = setTimeout(onReady, 200)
      pre.decode?.().then(onReady).catch(onReady)
    }
  }

  if (!imageSrc) return null

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="p-0 sm:p-0 bg-transparent border-0 shadow-none max-w-[min(96vw,1100px)]">
        <DialogTitle className="sr-only">Image viewer</DialogTitle>
        <DialogDescription className="sr-only">{imageAlt || 'Full image view'}</DialogDescription>
        <div 
          className="w-full flex items-center justify-center overscroll-contain overflow-x-hidden touch-pan-y"
          onClick={onClose}
        >
          <div className="relative bg-background/95 border border-muted/30 shadow-lg rounded-md p-2 md:p-3 max-w-[min(96vw,1100px)] max-h-[92vh]">
            {/* Show loading state */}
            {(!renderSrc || isLoading) && (
              <Image
                src={imageSrc}
                alt={imageAlt || 'Loading...'}
                width={1200}
                height={1600}
                className="max-h-[86vh] w-auto object-contain opacity-70"
                sizes="100vw"
                onClick={(e) => e.stopPropagation()}
              />
            )}
            {renderSrc && (
              <Image
                key={renderSrc}
                src={renderSrc}
                alt={imageAlt || "Full image"}
                width={1600}
                height={2000}
                className="max-h-[86vh] w-auto object-contain"
                sizes="100vw"
                onClick={(e) => e.stopPropagation()}
                onError={() => {
                  if (retry < 1) {
                    setRetry(retry + 1)
                    setRenderSrc(imageSrc + `?cb=${Date.now()}`)
                  }
                }}
              />
            )}
            {/* Image caption */}
            {imageAlt && (
              <div className="mt-2 text-center text-sm text-muted-foreground">
                {imageAlt}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
